import { DataInput } from '@/types/automationFields.ts';
import {
  getDataInputOptionValue,
  Option,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/hooks/useDataInputOptions.ts';

const BINDING_NAME_MAX_LENGTH = 64;
const BINDING_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const PYTHON_KEYWORDS = new Set([
  'False',
  'None',
  'True',
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'finally',
  'for',
  'from',
  'global',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'nonlocal',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'try',
  'while',
  'with',
  'yield',
]);

export function getCodeInputBindingError(bindingName: string | null | undefined): string | null {
  if (!bindingName) {
    return 'Code variable name is required.';
  }
  if (bindingName.length > BINDING_NAME_MAX_LENGTH) {
    return `Code variable name must be at most ${BINDING_NAME_MAX_LENGTH} characters.`;
  }
  if (!BINDING_NAME_PATTERN.test(bindingName)) {
    return 'Use letters, numbers, and underscores. Start with a letter or underscore.';
  }
  if (PYTHON_KEYWORDS.has(bindingName)) {
    return 'Code variable name cannot be a Python keyword.';
  }
  return null;
}

export function getCodeVariableNameErrorMessage(message: string): string {
  return message.replaceAll('Code input name', 'Code variable name');
}

export function getDuplicateBindingIndexes(dataInputs: DataInput[]): Set<number> {
  const firstIndexByBindingName = new Map<string, number>();
  const duplicateIndexes = new Set<number>();

  dataInputs.forEach((dataInput, index) => {
    const bindingName = dataInput.binding_name;
    if (!bindingName) {
      return;
    }

    const firstIndex = firstIndexByBindingName.get(bindingName);
    if (firstIndex === undefined) {
      firstIndexByBindingName.set(bindingName, index);
      return;
    }

    duplicateIndexes.add(firstIndex);
    duplicateIndexes.add(index);
  });

  return duplicateIndexes;
}

export function makeCodeInputBindingName(label: string, usedBindingNames: Set<string>): string {
  const base = slugToIdentifier(label) || 'input';
  let candidate = base;
  let suffix = 2;

  while (usedBindingNames.has(candidate)) {
    const suffixText = `_${suffix}`;
    candidate = `${base.slice(0, BINDING_NAME_MAX_LENGTH - suffixText.length)}${suffixText}`;
    suffix += 1;
  }

  usedBindingNames.add(candidate);
  return candidate;
}

export function ensureCodeInputBindingNames(
  dataInputs: DataInput[],
  options: Option[],
): DataInput[] {
  const optionByValue = new Map(options.map((option) => [option.value, option]));
  const usedBindingNames = new Set<string>();

  return dataInputs.map((dataInput) => {
    if (dataInput.binding_name) {
      usedBindingNames.add(dataInput.binding_name);
      return dataInput;
    }

    const option = optionByValue.get(getDataInputOptionValue(dataInput.config));
    const label = dataInput.name || dataInput.last_known_display_name || option?.label || 'input';
    return {
      ...dataInput,
      binding_name: makeCodeInputBindingName(label, usedBindingNames),
    };
  });
}

export function haveCodeInputBindingNamesChanged(first: DataInput[], second: DataInput[]): boolean {
  return first.some((dataInput, index) => dataInput.binding_name !== second[index]?.binding_name);
}

export function hasCodeInputBindingIssues(dataInputs: DataInput[]): boolean {
  const hasInvalidExistingBindingName = dataInputs.some(
    (dataInput) => dataInput.binding_name && getCodeInputBindingError(dataInput.binding_name),
  );
  if (hasInvalidExistingBindingName) {
    return true;
  }
  return getDuplicateBindingIndexes(dataInputs).size > 0;
}

export function shouldShowCodeInputBindingFields({
  requireCodeInputNames,
  isProvidedCode,
  hasBindingIssues,
}: {
  requireCodeInputNames: boolean;
  isProvidedCode: boolean;
  hasBindingIssues: boolean;
}): boolean {
  return requireCodeInputNames && (isProvidedCode || hasBindingIssues);
}

function slugToIdentifier(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, BINDING_NAME_MAX_LENGTH)
    .replace(/_+$/g, '');

  const identifier = /^[a-z_]/.test(slug) ? slug : `input_${slug}`;
  const cleanedIdentifier = (identifier || 'input')
    .slice(0, BINDING_NAME_MAX_LENGTH)
    .replace(/_+$/g, '');
  if (!PYTHON_KEYWORDS.has(cleanedIdentifier)) {
    return cleanedIdentifier;
  }
  return `${cleanedIdentifier}_input`.slice(0, BINDING_NAME_MAX_LENGTH);
}
